import styled, { keyframes } from "styled-components";
import Button from "./Button";
import { PortalSelect, PortalSurface, portalColors } from "./BusinessPortalComponents";

const shimmer = keyframes`
    0% {
        background-position: 100% 0;
    }

    100% {
        background-position: -100% 0;
    }
`;

const feedBackgroundDrift = keyframes`
    0% {
        background-position: -4vw -3vh, 4vw 2vh, -20vw -20vh, 0 0;
    }

    50% {
        background-position: 4vw 3vh, -4vw 7vh, -14vw -14vh, 0 0;
    }

    100% {
        background-position: -2vw 7vh, 2vw -4vh, -8vw -18vh, 0 0;
    }
`;

export const NoticeboardViewCard = styled.div`
    min-width: 0;
    min-height: 7rem;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.65rem;
    border: 1px solid ${portalColors.border};
    border-radius: 12px;
    background: linear-gradient(135deg, ${portalColors.blueSoft} 0%, ${portalColors.surface} 78%);
    box-sizing: border-box;
`;

export const NoticeboardViewCardTitle = styled.h2`
    margin: 0;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9rem;
    font-weight: 800;
    line-height: 1.25;
`;

export const NoticeboardViewCardText = styled.p`
    margin: 0;
    color: ${portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.45;
`;

export const NoticeboardViewCardButton = styled(Button)`
    width: ${p => p.$fullWidth ? "100%" : "auto"};
    min-width: 8.75rem;
    height: 2.25rem;
    padding: 0 0.85rem;
    border: 1px solid ${portalColors.blue};
    border-radius: 7px !important;
    background: ${portalColors.blue};
    color: ${portalColors.actionPrimaryText};
    font-size: 0.75rem;
    font-weight: 800;

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }
`;

export const FeedShell = styled.div`
    width: 100%;
    padding: var(--feed-card-gutter) 0;
    display: grid;
    gap: 1rem;
    box-sizing: border-box;
`;

export const FeedPageBackground = styled.section`
    --feed-card-gutter: 1.25rem;

    position: relative;
    isolation: isolate;
    width: 100%;
    height: calc(100dvh - 8rem);
    min-height: min(32rem, calc(100vh - 8rem));
    min-width: 0;
    overflow: hidden;
    margin-top: var(--portal-page-gutter, 1rem);
    padding: 0 var(--feed-card-gutter);
    border: 1px solid ${portalColors.border};
    border-radius: 20px;
    background-image:
        radial-gradient(ellipse at 12% 8%, rgba(91, 110, 245, 0.13) 0 13%, transparent 42%),
        radial-gradient(ellipse at 88% 30%, rgba(87, 205, 190, 0.1) 0 12%, transparent 38%),
        repeating-radial-gradient(
            circle at 12% 25%,
            transparent 0 38px,
            rgba(91, 110, 245, 0.055) 40px 42px,
            transparent 44px 70px
        ),
        linear-gradient(145deg, var(--calendar-surface-secondary, #F6F8FC), var(--calendar-bg, #FFFFFF));
    background-position: -4vw -3vh, 4vw 2vh, -20vw -20vh, 0 0;
    background-repeat: no-repeat;
    background-size: 120vw 105vh, 120vw 105vh, 140vw 140vh, 100% 100%;
    background-attachment: fixed;
    animation: ${feedBackgroundDrift} 28s ease-in-out infinite alternate;
    box-sizing: border-box;

    [data-theme="dark"] & {
        background-image:
            radial-gradient(ellipse at 12% 8%, rgba(118, 132, 255, 0.14) 0 13%, transparent 42%),
            radial-gradient(ellipse at 88% 30%, rgba(87, 205, 190, 0.08) 0 12%, transparent 38%),
            repeating-radial-gradient(
                circle at 12% 25%,
                transparent 0 38px,
                rgba(158, 169, 255, 0.045) 40px 42px,
                transparent 44px 70px
            ),
            linear-gradient(145deg, var(--calendar-shell, #151924), var(--calendar-bg, #10131B));
        background-attachment: fixed;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }

    @media (max-width: 1080px) {
        height: auto;
        min-height: 0;
    }

    @media (max-width: 640px) {
        --feed-card-gutter: 0.75rem;

        border-radius: 14px;
    }
`;

export const FeedSidebarColumn = styled.aside`
    position: static;
    height: 100%;
    min-width: 0;
    min-height: 0;
    max-height: none;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding: var(--feed-card-gutter) 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    box-sizing: border-box;

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: -3px;
    }

    @media (max-width: 1080px) {
        height: auto;
        overflow: visible;
        scrollbar-gutter: auto;
    }
`;

export const FeedHeaderIntro = styled.div`
    min-width: 0;
    padding: 1.35rem 1.5rem;
    align-content: center;
    display: grid;
    gap: 0.5rem;
    border: 1px solid ${portalColors.border};
    border-radius: 16px;
    background: color-mix(in srgb, ${portalColors.surface} 94%, transparent);
    box-shadow: ${portalColors.shadow};
    backdrop-filter: blur(8px);

    @media (max-width: 640px) {
        padding: 1.1rem;
    }
`;

export const FeedPageTitle = styled.h1`
    margin: 0;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: clamp(1.55rem, 3vw, 2rem);
    font-weight: 800;
    line-height: 1.15;
`;

export const FeedPageDescription = styled.p`
    max-width: 43rem;
    margin: 0;
    color: ${portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9rem;
    font-weight: 500;
    line-height: 1.55;
`;

export const FeedMainGrid = styled.div`
    position: relative;
    z-index: 1;
    height: 100%;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(17rem, 22rem) minmax(0, 46rem);
    justify-content: start;
    align-items: stretch;
    gap: 1rem;

    @media (max-width: 1080px) {
        height: auto;
        overflow: visible;
        grid-template-columns: 1fr;
        align-items: start;
    }
`;

export const FeedColumn = styled.div`
    height: 100%;
    min-width: 0;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding-right: 0.5rem;
    display: grid;
    gap: 1rem;
    box-sizing: border-box;

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: -3px;
    }

    @media (max-width: 1080px) {
        height: auto;
        overflow: visible;
        scrollbar-gutter: auto;
        padding-right: 0;
    }
`;

export const FeedControlLabel = styled.span`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
`;

export const FeedAudienceSelect = styled(PortalSelect)`
    min-width: 12rem;
    margin-top: 0.35rem;
    display: block;

    @media (max-width: 620px) {
        width: 100%;
    }
`;

export const FeedCardSurface = styled.article`
    position: relative;
    z-index: 1;
    width: 100%;
    min-width: 0;
    overflow: visible;
    border: 1px solid ${portalColors.border};
    border-radius: 16px;
    background: ${portalColors.surface};
    box-shadow: ${portalColors.shadow};
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    box-sizing: border-box;
    transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;

    &:hover {
        z-index: 2;
        border-color: ${portalColors.borderStrong};
        box-shadow: 0 22px 44px rgba(29, 42, 89, 0.11);
        transform: translateY(-1px);
    }

    &:focus-within {
        z-index: 5;
        border-color: ${portalColors.blue};
    }

    @media (max-width: 640px) {
        border-radius: 12px;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        &:hover {
            transform: none;
        }
    }
`;

export const FeedCardHeader = styled.header`
    padding: 1.1rem ${p => p.$hasPinButton ? "4.5rem" : "1.15rem"} 0.85rem 1.15rem;
    display: grid;
    grid-template-columns: ${p => p.$hasAvatar === false ? "minmax(0, 1fr)" : "auto minmax(0, 1fr)"};
    align-items: center;
    gap: 0.75rem;
`;

export const FeedPinIconButton = styled.button`
    position: absolute;
    z-index: 6;
    top: ${p => p.$top || "0.9rem"};
    right: ${p => p.$right || "1rem"};
    bottom: ${p => p.$bottom || "auto"};
    left: ${p => p.$left || "auto"};
    width: 2.6rem;
    height: 2.6rem;
    padding: 0.48rem;
    display: grid;
    place-items: center;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 50%;
    background: ${portalColors.surface};
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
    cursor: pointer;
    transition: background 0.14s ease, border-color 0.14s ease, transform 0.14s ease;

    &:hover {
        border-color: ${portalColors.borderStrong};
        background: ${portalColors.surfaceSoft};
        transform: translateY(-1px);
    }

    &:active {
        transform: scale(0.96);
    }

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }

    &:disabled {
        cursor: wait;
        opacity: 0.55;
    }

    &:disabled:hover {
        border-color: ${portalColors.borderStrong};
        background: ${portalColors.surface};
        transform: none;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

export const FeedPinIcon = styled.img`
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;

    [data-theme="dark"] & {
        filter: ${p => p.$active ? "brightness(1.45)" : "invert(1) brightness(0.9)"};
    }
`;

export const FeedAvatar = styled.div`
    width: ${p => p.$size || "2.75rem"};
    height: ${p => p.$size || "2.75rem"};
    min-width: ${p => p.$size || "2.75rem"};
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 50%;
    background: ${portalColors.blueSoft};
    color: ${portalColors.text};
    font-size: ${p => p.$fontSize || "0.8rem"};
    font-weight: 800;
    line-height: 1;
    box-sizing: border-box;
`;

export const FeedAvatarImage = styled.img`
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
`;

export const FeedIdentity = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.2rem;
`;

export const FeedCreator = styled.div`
    min-width: 0;
    color: ${portalColors.text};
    font-size: 0.875rem;
    font-weight: 750;
    line-height: 1.35;
    overflow-wrap: anywhere;
`;

export const FeedMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
    color: ${portalColors.mutedText};
    font-size: 0.75rem;
    font-weight: 550;
    line-height: 1.35;
`;

export const FeedBadge = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 1.45rem;
    padding: 0.15rem 0.5rem;
    border: 1px solid ${portalColors.border};
    border-radius: 999px;
    background: ${p => p.$important ? "rgba(214, 69, 69, 0.1)" : portalColors.surfaceSoft};
    color: ${p => p.$important ? "#B33131" : portalColors.mutedText};
    font-size: 0.68rem;
    font-weight: 800;
    box-sizing: border-box;
`;

export const FeedCardBody = styled.div`
    padding: 0 1.15rem 1rem;
`;

export const FeedPostButton = styled.button`
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 4px;
        border-radius: 6px;
    }
`;

export const FeedTitle = styled.h2`
    margin: 0 0 0.55rem;
    color: ${portalColors.text};
    font-size: 1.12rem;
    font-weight: 800;
    line-height: 1.35;
    overflow-wrap: anywhere;
`;

export const FeedDescription = styled.div`
    color: ${portalColors.text};
    font-size: 0.91rem;
    font-weight: 500;
    line-height: 1.62;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

export const FeedTextButton = styled.button`
    margin: 0.35rem 0 0;
    padding: 0.15rem 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: ${portalColors.blue};
    font: inherit;
    font-size: 0.8rem;
    font-weight: 800;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }
`;

export const FeedMediaFrame = styled.div`
    position: relative;
    width: 100%;
    height: auto;
    min-height: ${p => p.$loading ? "18rem" : "0"};
    flex: 0 0 auto;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => p.$detail ? "transparent" : portalColors.surfaceSoft};
    border-top: ${p => p.$detail ? "0" : `1px solid ${portalColors.border}`};
    border-bottom: ${p => p.$detail ? "0" : `1px solid ${portalColors.border}`};

    @media (max-width: 640px) {
        min-height: ${p => p.$loading ? "18rem" : "0"};
    }
`;

export const FeedImage = styled.img`
    width: 100%;
    height: auto;
    max-width: 100%;
    max-height: ${p => p.$detail ? "none" : "min(78vh, 52rem)"};
    display: block;
    object-fit: contain;
    background: ${p => p.$detail ? "transparent" : portalColors.surfaceSoft};
`;

export const FeedVideo = styled.video`
    width: 100%;
    height: auto;
    max-width: 100%;
    max-height: ${p => p.$detail ? "none" : "min(78vh, 52rem)"};
    display: block;
    object-fit: contain;
    background: #000;
`;

export const FeedMediaButton = styled.button`
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;

    &:focus-visible {
        outline: 3px solid ${portalColors.blue};
        outline-offset: -3px;
    }
`;

export const FeedCarouselButton = styled.button`
    position: absolute;
    z-index: 2;
    top: 50%;
    ${p => p.$side === "left" ? "left: 0.75rem;" : "right: 0.75rem;"}
    width: 2.5rem;
    height: 2.5rem;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: 50%;
    background: rgba(18, 22, 31, 0.72);
    color: #fff;
    font-size: 1.2rem;
    cursor: pointer;
    transform: translateY(-50%);

    &:focus-visible {
        outline: 3px solid #fff;
        outline-offset: 2px;
    }
`;

export const FeedCarouselCount = styled.span`
    position: absolute;
    z-index: 2;
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.3rem 0.6rem;
    border-radius: 999px;
    background: rgba(18, 22, 31, 0.72);
    color: #fff;
    font-size: 0.72rem;
    font-weight: 800;
`;

export const FeedMediaExpandButton = styled.button`
    position: absolute;
    z-index: 3;
    left: 0.75rem;
    top: 0.75rem;
    min-height: 2rem;
    padding: 0.35rem 0.65rem;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: 7px;
    background: rgba(18, 22, 31, 0.72);
    color: #fff;
    font: inherit;
    font-size: 0.7rem;
    font-weight: 800;
    cursor: pointer;

    &:focus-visible {
        outline: 3px solid #fff;
        outline-offset: 2px;
    }
`;

export const FeedMediaError = styled.div`
    min-height: 11rem;
    padding: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${portalColors.mutedText};
    font-size: 0.875rem;
    text-align: center;
`;

export const FeedAttachmentSection = styled.section`
    padding: 0.9rem 1.15rem;
    display: grid;
    gap: 0.55rem;
    border-bottom: 1px solid ${portalColors.border};
`;

export const FeedSectionLabel = styled.h3`
    margin: 0;
    color: ${portalColors.mutedText};
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

export const FeedAttachmentLink = styled.a`
    min-width: 0;
    padding: 0.65rem 0.75rem;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.65rem;
    border: 1px solid ${portalColors.border};
    border-radius: 9px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    font-size: 0.8rem;
    font-weight: 700;
    text-decoration: none;

    &:hover {
        border-color: ${portalColors.blue};
    }

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }
`;

export const FeedAttachmentName = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const FeedSummaryRow = styled.div`
    min-height: 2.85rem;
    padding: 0.55rem 1.15rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border-bottom: 1px solid ${portalColors.border};
    box-sizing: border-box;
`;

export const FeedSummaryButton = styled.button`
    min-width: 0;
    padding: 0.25rem;
    border: 0;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: transparent;
    color: ${portalColors.mutedText};
    font-size: 0.78rem;
    font-weight: 650;
    cursor: pointer;

    &:hover {
        color: ${portalColors.text};
        text-decoration: underline;
    }

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }
`;

export const FeedSummaryText = styled.span`
    min-width: 0;
    padding: 0.25rem;
    color: ${portalColors.mutedText};
    font-size: 0.78rem;
    font-weight: 650;
    line-height: normal;
    text-align: right;
`;

export const FeedActionRow = styled.div`
    padding: 0.55rem 1.15rem;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    border-bottom: 1px solid ${portalColors.border};
`;

export const FeedReactionPicker = styled.div`
    position: relative;
    padding: 0.15rem 1.15rem 0.65rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    border-bottom: 1px solid ${portalColors.border};
`;

export const FeedReactionQuickList = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    flex-wrap: wrap;
`;

export const FeedMoreReactionButton = styled.button`
    min-height: 2.2rem;
    padding: 0.35rem 0.65rem;
    border: 1px solid ${portalColors.border};
    border-radius: 999px;
    background: ${portalColors.surface};
    color: ${portalColors.text};
    font: inherit;
    font-size: 0.72rem;
    font-weight: 800;
    cursor: pointer;

    &:hover {
        border-color: ${portalColors.blue};
        background: ${portalColors.blueSoft};
    }

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }
`;

export const FeedReactionPopup = styled.div`
    position: absolute;
    z-index: 30;
    left: 50%;
    bottom: calc(100% + 0.4rem);
    width: min(22rem, calc(100vw - 3rem));
    padding: 0.65rem;
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 0.25rem;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 12px;
    background: ${portalColors.surface};
    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.22);
    transform: translateX(-50%);

    @media (max-width: 520px) {
        grid-template-columns: repeat(6, minmax(0, 1fr));
    }
`;

export const FeedReactionChoice = styled.button`
    width: 2.35rem;
    height: 2.35rem;
    padding: 0;
    border: 1px solid ${p => p.$active || p["aria-pressed"] ? `var(--checked-in-primary, ${portalColors.blue})` : "transparent"};
    border-radius: 50%;
    background: ${p => p.$active || p["aria-pressed"] ? `var(--checked-in-background, ${portalColors.blueSoft})` : "transparent"};
    font-size: 1.2rem;
    cursor: pointer;
    transition: background 0.14s ease, transform 0.14s ease;

    &:hover {
        background: ${portalColors.surfaceSoft};
        transform: translateY(-2px);
    }

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        &:hover {
            transform: none;
        }
    }
`;

export const PinnedSidebar = styled.aside`
    position: static;
    min-height: 0;
    min-width: 0;
    max-height: none;
    flex: 0 0 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid ${portalColors.border};
    border-radius: 16px;
    background: color-mix(in srgb, ${portalColors.surface} 96%, transparent);
    box-shadow: ${portalColors.shadow};
    backdrop-filter: blur(8px);

    @media (max-width: 1080px) {
        flex: none;
        max-height: none;
    }
`;

export const PinnedHeader = styled.div`
    padding: 0.9rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    border-bottom: 1px solid ${portalColors.border};
`;

export const PinnedTitle = styled.h2`
    margin: 0;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.92rem;
    font-weight: 800;
`;

export const PinnedList = styled.div`
    min-height: 0;
    padding: 0.65rem;
    display: grid;
    gap: 0.6rem;
    overflow-y: visible;

    @media (max-width: 1080px) {
        grid-auto-flow: column;
        grid-auto-columns: minmax(15rem, 18rem);
        overflow-x: auto;
        overflow-y: hidden;
        scroll-snap-type: x proximity;
    }
`;

export const PinnedPostItem = styled.div`
    position: relative;
    min-width: 0;
    scroll-snap-align: start;
`;

export const PinnedPostButton = styled.button`
    width: 100%;
    min-height: ${p => p.$hasPinButton ? "7.9rem" : "0"};
    min-width: 0;
    padding: 0.65rem;
    display: grid;
    grid-template-columns: 4rem minmax(0, 1fr);
    align-items: start;
    gap: 0.65rem;
    border: 1px solid ${portalColors.border};
    border-radius: 10px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    text-align: left;
    cursor: pointer;
    scroll-snap-align: start;
    transition: border-color 0.16s ease, transform 0.16s ease;

    &:hover {
        border-color: ${portalColors.blue};
        transform: translateY(-1px);
    }

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

export const PinnedThumbnail = styled.div`
    width: 4rem;
    height: 4rem;
    overflow: hidden;
    display: grid;
    place-items: center;
    border-radius: 8px;
    background: ${portalColors.surfaceTertiary};
    color: ${portalColors.mutedText};
    font-size: 1.1rem;
`;

export const PinnedThumbnailImage = styled.img`
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
`;

export const PinnedContent = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.25rem;
`;

export const PinnedPostTitle = styled.div`
    display: -webkit-box;
    overflow: hidden;
    color: ${portalColors.text};
    font-size: 0.78rem;
    font-weight: 800;
    line-height: 1.35;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
`;

export const PinnedDescription = styled.div`
    display: -webkit-box;
    overflow: hidden;
    color: ${portalColors.mutedText};
    font-size: 0.68rem;
    font-weight: 500;
    line-height: 1.4;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
`;

export const PinnedCreatorRow = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: ${portalColors.mutedText};
    font-size: 0.64rem;
    font-weight: 700;
`;

export const PinnedMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex-wrap: wrap;
    color: ${portalColors.mutedText};
    font-size: 0.62rem;
    font-weight: 650;
`;

export const PinnedState = styled.div`
    padding: 1rem;
    color: ${p => p.$error ? "#B33131" : portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.76rem;
    font-weight: 600;
    line-height: 1.5;
    text-align: center;
`;

export const FeedActionButton = styled.button`
    min-height: 2.45rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid ${p => p.$active ? portalColors.blue : "transparent"};
    border-radius: 8px;
    background: ${p => p.$active ? portalColors.blueSoft : "transparent"};
    color: ${p => p.$active ? portalColors.blue : portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.82rem;
    font-weight: 800;
    cursor: pointer;
    transition: background 0.14s ease, transform 0.14s ease;

    &:hover {
        background: ${portalColors.surfaceSoft};
    }

    &:active {
        transform: scale(0.98);
    }

    &:focus-visible {
        outline: 3px solid ${portalColors.blueSoft};
        outline-offset: 2px;
    }

    &:disabled {
        background: transparent;
        color: ${portalColors.mutedText};
        cursor: not-allowed;
        opacity: 0.45;
    }

    &:disabled:hover {
        background: transparent;
    }

    &:disabled:active {
        transform: none;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

export const FeedCommentPreviewList = styled.div`
    padding: 0.8rem 1.15rem 1rem;
    display: grid;
    gap: 0.75rem;
`;

export const FeedComment = styled.div`
    min-width: 0;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: flex-start;
    gap: 0.6rem;
`;

export const FeedCommentBubble = styled.div`
    min-width: 0;
    padding: 0.58rem 0.7rem;
    border-radius: 10px;
    background: ${portalColors.surfaceSoft};
`;

export const FeedCommentName = styled.div`
    color: ${portalColors.text};
    font-size: 0.75rem;
    font-weight: 800;
    line-height: 1.35;
`;

export const FeedCommentText = styled.div`
    margin-top: 0.12rem;
    color: ${portalColors.text};
    font-size: 0.8rem;
    font-weight: 500;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

export const FeedCommentMeta = styled.div`
    margin-top: 0.3rem;
    color: ${portalColors.mutedText};
    font-size: 0.68rem;
    font-weight: 600;
`;

export const FeedInlineState = styled.div`
    padding: ${p => p.$compact ? "0.25rem 0" : "1rem"};
    color: ${p => p.$error ? "#B33131" : portalColors.mutedText};
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.45;
    text-align: center;
`;

export const FeedStateSurface = styled(PortalSurface)`
    min-height: 15rem;
    padding: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
`;

export const FeedStateContent = styled.div`
    max-width: 28rem;
    display: grid;
    justify-items: center;
    gap: 0.7rem;
`;

export const FeedStateTitle = styled.h2`
    margin: 0;
    color: ${portalColors.text};
    font-size: 1.05rem;
    font-weight: 800;
`;

export const FeedStateText = styled.p`
    margin: 0;
    color: ${portalColors.mutedText};
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.55;
`;

export const FeedRetryButton = styled(NoticeboardViewCardButton)``;

export const FeedSkeletonSurface = styled(FeedCardSurface)`
    padding: 1.15rem;
    display: grid;
    gap: 0.8rem;
    pointer-events: none;
`;

export const FeedSkeletonRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;

export const FeedSkeletonTextStack = styled.div`
    flex: 1;
    display: grid;
    gap: 0.4rem;
`;

export const FeedSkeletonBlock = styled.div`
    width: ${p => p.$width || "100%"};
    height: ${p => p.$height || "1rem"};
    border-radius: ${p => p.$radius || "7px"};
    background: linear-gradient(
        90deg,
        ${portalColors.surfaceSoft} 20%,
        ${portalColors.surfaceTertiary} 40%,
        ${portalColors.surfaceSoft} 60%
    );
    background-size: 220% 100%;
    animation: ${shimmer} 1.25s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

export const FeedLoadSentinel = styled.div`
    min-height: 1px;
`;

export const FeedEndState = styled(FeedStateText)`
    padding: 0.75rem 0 1.5rem;
    text-align: center;
`;

export const FeedModalList = styled.div`
    display: grid;
    gap: 0.85rem;
`;

export const FeedReactionRow = styled.div`
    min-width: 0;
    padding: 0.7rem;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.75rem;
    border: 1px solid ${portalColors.border};
    border-radius: 10px;
    background: ${portalColors.surface};
`;

export const FeedReactionName = styled.div`
    min-width: 0;
    color: ${portalColors.text};
    font-size: 0.86rem;
    font-weight: 750;
    overflow-wrap: anywhere;
`;

export const FeedReactionEmoji = styled.span`
    font-size: 1.25rem;
`;

export const FeedReactionFilters = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`;

export const FeedReactionFilter = styled.button`
    min-height: 2.1rem;
    padding: 0.35rem 0.7rem;
    border: 1px solid ${p => p.$active ? portalColors.blue : portalColors.border};
    border-radius: 999px;
    background: ${p => p.$active ? portalColors.blueSoft : portalColors.surface};
    color: ${portalColors.text};
    font: inherit;
    font-size: 0.75rem;
    font-weight: 750;
    cursor: pointer;
`;

export const FeedComposer = styled.form`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    gap: 0.65rem;
`;

export const FeedComposerInput = styled.textarea`
    width: 100%;
    min-height: 3rem;
    max-height: 9rem;
    padding: 0.7rem 0.8rem;
    resize: vertical;
    border: 1px solid ${portalColors.inputBorder};
    border-radius: 9px;
    background: ${portalColors.surface};
    color: ${portalColors.text};
    font: inherit;
    font-size: 0.85rem;
    line-height: 1.45;
    box-sizing: border-box;

    &:focus {
        outline: 3px solid ${portalColors.blueSoft};
        border-color: ${portalColors.blue};
    }
`;

export const FeedComposerButton = styled(NoticeboardViewCardButton)`
    height: 3rem;
`;

export const FeedDetailComposerDock = styled.div`
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
    padding: 0.85rem 1.15rem 1rem;
    border-top: 1px solid ${portalColors.borderStrong};
    background: ${portalColors.surface};
    box-shadow: 0 -10px 24px rgba(15, 23, 42, 0.08);
`;

export const FeedReplyButton = styled(FeedTextButton)`
    margin: 0.25rem 0 0;
    font-size: 0.7rem;

    & + & {
        margin-left: 0.65rem;
    }
`;

export const FeedReplyList = styled.div`
    margin: 0.65rem 0 0 1rem;
    padding-left: 0.8rem;
    display: grid;
    gap: 0.65rem;
    border-left: 2px solid ${portalColors.border};
`;

export const FeedDetailSection = styled.section`
    padding: ${p => p.$padding || "0"};
    display: grid;
    gap: 0.75rem;
`;

export const FeedDetailMedia = styled.div`
    overflow: hidden;
    border: 0;
    border-radius: 0;
    background: transparent;
`;
